import { MARKUP_COLORS, type MarkupShape } from "@nmt/shared";
import { useEffect, useRef, useState } from "react";
import { cx } from "./ui";

type Tool = MarkupShape["kind"];
const TOOLS: { kind: Tool; label: string }[] = [
  { kind: "arrow", label: "Arrow" },
  { kind: "circle", label: "Circle" },
  { kind: "box", label: "Box" },
  { kind: "text", label: "Text" },
];

/** The size an element is drawn at, kept up to date. */
function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

/** The shapes in pixels over a w × h image. */
function Shapes({ shapes, w, h }: { shapes: MarkupShape[]; w: number; h: number }) {
  const stroke = Math.max(2, Math.min(w, h) / 120);
  return (
    <>
      <svg className="pointer-events-none absolute inset-0" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
        {shapes.map((s, i) => {
          const [x1, y1, x2, y2] = [s.x1 * w, s.y1 * h, s.x2 * w, s.y2 * h];
          if (s.kind === "arrow") {
            const a = Math.atan2(y2 - y1, x2 - x1);
            const head = stroke * 5;
            const p = (d: number) => `${x2 + head * Math.cos(a + d)},${y2 + head * Math.sin(a + d)}`;
            return (
              <g key={i} stroke={s.color} strokeWidth={stroke} strokeLinecap="round" fill="none">
                <line x1={x1} y1={y1} x2={x2} y2={y2} />
                <polyline points={`${p(Math.PI * 0.85)} ${x2},${y2} ${p(-Math.PI * 0.85)}`} />
              </g>
            );
          }
          if (s.kind === "circle") return <ellipse key={i} cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} rx={Math.abs(x2 - x1) / 2} ry={Math.abs(y2 - y1) / 2} stroke={s.color} strokeWidth={stroke} fill="none" />;
          if (s.kind === "box") return <rect key={i} x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={Math.abs(x2 - x1)} height={Math.abs(y2 - y1)} stroke={s.color} strokeWidth={stroke} fill="none" />;
          return null;
        })}
      </svg>
      {shapes
        .filter((s) => s.kind === "text" && s.text)
        .map((s, i) => (
          <span key={`t${i}`} className="pointer-events-none absolute rounded bg-black/55 px-1 font-semibold whitespace-nowrap" style={{ left: s.x1 * w, top: s.y1 * h, color: s.color, fontSize: Math.max(11, Math.min(w, h) / 22) }}>
            {s.text}
          </span>
        ))}
    </>
  );
}

/** A photo with its markup drawn over it. */
export function MarkedPhoto({ src, alt, shapes, className }: { src: string; alt: string; shapes: MarkupShape[]; className?: string }) {
  const [ref, size] = useSize<HTMLImageElement>();
  return (
    <span className="relative inline-block">
      <img ref={ref} src={src} alt={alt} className={cx("block", className)} />
      {size.w > 0 && <Shapes shapes={shapes} w={size.w} h={size.h} />}
    </span>
  );
}

/** Draw arrows, circles, boxes, and labels over a photo. The photo itself is never changed. */
export function MarkupEditor({ src, alt, shapes, onChange, className }: { src: string; alt: string; shapes: MarkupShape[]; onChange: (s: MarkupShape[]) => void; className?: string }) {
  const [ref, size] = useSize<HTMLImageElement>();
  const [tool, setTool] = useState<Tool>("arrow");
  const [color, setColor] = useState(MARKUP_COLORS[0]!);
  const [draft, setDraft] = useState<MarkupShape | null>(null);
  const pos = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const round = (n: number) => Math.round(n * 10_000) / 10_000;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div role="radiogroup" aria-label="Draw" className="inline-flex rounded-[var(--radius-control)] bg-mist p-0.5">
          {TOOLS.map((t) => (
            <button key={t.kind} type="button" role="radio" aria-checked={tool === t.kind} onClick={() => setTool(t.kind)} className={cx("rounded-[6px] px-3 py-1 text-sm font-semibold", tool === t.kind ? "bg-white shadow-sm" : "text-muted hover:text-ink")}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1" role="radiogroup" aria-label="Color">
          {MARKUP_COLORS.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} onClick={() => setColor(c)} className={cx("size-7 rounded-full border-2", color === c ? "border-ink" : "border-line")} style={{ background: c }} />
          ))}
        </div>
        <button type="button" className="text-sm font-semibold text-ink-soft hover:text-ink disabled:opacity-40" disabled={!shapes.length} onClick={() => onChange(shapes.slice(0, -1))}>
          Undo
        </button>
        <button type="button" className="text-sm text-muted hover:text-ink disabled:opacity-40" disabled={!shapes.length} onClick={() => onChange([])}>
          Clear
        </button>
      </div>
      <div className="self-center">
        <span
          className="relative inline-block touch-none select-none"
          onPointerDown={(e) => {
            const p = pos(e);
            if (tool === "text") {
              const text = window.prompt("Label")?.trim().slice(0, 80);
              if (text) onChange([...shapes, { kind: "text", x1: round(p.x), y1: round(p.y), x2: round(p.x), y2: round(p.y), color, text }]);
              return;
            }
            (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
            setDraft({ kind: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
          }}
          onPointerMove={(e) => draft && setDraft({ ...draft, x2: pos(e).x, y2: pos(e).y })}
          onPointerUp={() => {
            if (draft && Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 0.01) onChange([...shapes, { ...draft, x1: round(draft.x1), y1: round(draft.y1), x2: round(draft.x2), y2: round(draft.y2) }]);
            setDraft(null);
          }}
        >
          <img ref={ref} src={src} alt={alt} draggable={false} className={cx("block cursor-crosshair", className)} />
          {size.w > 0 && <Shapes shapes={draft ? [...shapes, draft] : shapes} w={size.w} h={size.h} />}
        </span>
      </div>
      <p className="text-xs text-muted">Drag on the photo to draw. The original photo isn't changed; the markup shows here and in photo reports.</p>
    </div>
  );
}
